import { useEffect, useMemo, useState } from "react";
import { CircleAlert, LoaderCircle, LockKeyhole, RefreshCw, Save, Search, ShieldCheck, UserRound } from "lucide-react";

import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard, SkeletonTable } from "@/components/skeleton";
import { apiFetch } from "@/lib/api-client";

type UserRole =
  | "super_admin"
  | "admin"
  | "customer_success"
  | "staff"
  | "teacher"
  | "affiliate"
  | "premium"
  | "paid"
  | "free"
  | "unregistered";

type UserStatus = "active" | "disabled";

interface AdminUserSummary {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  twoFactorEnabled: boolean;
  lastActiveAt: string | null;
  organizations: string[];
  banReason: string | null;
  banExpires: string | null;
}

interface AdminUserDetail extends AdminUserSummary {
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  users: AdminUserSummary[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

interface UserDetailResponse {
  user: AdminUserDetail;
}

interface UserQueryState {
  search: string;
  role: string;
  status: string;
  offset: number;
  selectedUserId: string | null;
}

interface EditUserFormState {
  role: UserRole;
  status: UserStatus;
}

const PAGE_SIZE = 20;

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All roles" },
  { value: "super_admin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "customer_success", label: "Customer Success" },
  { value: "staff", label: "Staff" },
  { value: "teacher", label: "Teacher" },
  { value: "affiliate", label: "Affiliate" },
  { value: "premium", label: "Premium" },
  { value: "paid", label: "Paid" },
  { value: "free", label: "Free" },
  { value: "unregistered", label: "Unregistered" },
];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

function readQueryState(): UserQueryState {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset") ?? "0");

  return {
    search: params.get("search")?.trim() ?? "",
    role: params.get("role")?.trim() || "all",
    status: params.get("status")?.trim() || "all",
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    selectedUserId: params.get("id"),
  };
}

function writeQueryState(next: UserQueryState) {
  const params = new URLSearchParams();

  if (next.search) {
    params.set("search", next.search);
  }

  if (next.role !== "all") {
    params.set("role", next.role);
  }

  if (next.status !== "all") {
    params.set("status", next.status);
  }

  if (next.offset > 0) {
    params.set("offset", String(next.offset));
  }

  if (next.selectedUserId) {
    params.set("id", next.selectedUserId);
  }

  const search = params.toString();
  window.history.replaceState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
}

function formatDateTime(value: string | null) {
  if (!value) {
    return "No session recorded";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function titleizeRole(role: string) {
  return role.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusTone(status: UserStatus) {
  return status === "active"
    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
    : "bg-rose-500/10 text-rose-300 border-rose-500/20";
}

function UsersSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-32" />
        <Skeleton className="mt-2 h-4 w-96" />
      </div>
      <div className="flex gap-3">
        <Skeleton className="h-10 flex-1" />
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-10 w-40" />
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <SkeletonTable rows={6} cols={6} />
        </div>
        <SkeletonCard className="h-[420px]" />
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const [query, setQuery] = useState<UserQueryState>(() => readQueryState());
  const [searchDraft, setSearchDraft] = useState(query.search);
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [pagination, setPagination] = useState<UsersResponse["pagination"]>({
    limit: PAGE_SIZE,
    offset: 0,
    total: 0,
    hasMore: false,
  });
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedUser, setSelectedUser] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditUserFormState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isBanning, setIsBanning] = useState(false);

  const selectedUserLabel = selectedUser ? `${selectedUser.name} (${selectedUser.email})` : "No user selected";

  useEffect(() => {
    const handlePopState = () => setQuery(readQueryState());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    setSearchDraft(query.search);
  }, [query.search]);

  useEffect(() => {
    let isCancelled = false;

    const loadUsers = async () => {
      setListLoading(true);
      setListError(null);

      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(query.offset),
      });

      if (query.search) params.set("search", query.search);
      if (query.role !== "all") params.set("role", query.role);
      if (query.status !== "all") params.set("status", query.status);

      try {
        const data = await apiFetch<UsersResponse>(`/platform/users?${params.toString()}`);
        if (isCancelled) return;

        setUsers(data.users);
        setPagination(data.pagination);

        if (query.selectedUserId && !data.users.some((user) => user.id === query.selectedUserId)) {
          const next = { ...query, selectedUserId: null };
          writeQueryState(next);
          setQuery(next);
        }
      } catch {
        if (!isCancelled) {
          setUsers([]);
          setPagination({ limit: PAGE_SIZE, offset: query.offset, total: 0, hasMore: false });
          setListError("User management is currently unavailable.");
        }
      } finally {
        if (!isCancelled) {
          setListLoading(false);
        }
      }
    };

    void loadUsers();
    return () => {
      isCancelled = true;
    };
  }, [query]);

  useEffect(() => {
    if (!query.selectedUserId) {
      setSelectedUser(null);
      setEditForm(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    let isCancelled = false;

    const loadUserDetail = async () => {
      setDetailLoading(true);
      setDetailError(null);
      setFormNotice(null);

      try {
        const data = await apiFetch<UserDetailResponse>(`/platform/users/${query.selectedUserId}`);
        if (isCancelled) return;

        setSelectedUser(data.user);
        setEditForm({
          role: data.user.role,
          status: data.user.status,
        });
      } catch {
        if (!isCancelled) {
          setSelectedUser(null);
          setEditForm(null);
          setDetailError("Selected user details are currently unavailable.");
        }
      } finally {
        if (!isCancelled) {
          setDetailLoading(false);
        }
      }
    };

    void loadUserDetail();
    return () => {
      isCancelled = true;
    };
  }, [query.selectedUserId]);

  function setQueryState(patch: Partial<UserQueryState>) {
    const next = { ...query, ...patch };
    writeQueryState(next);
    setQuery(next);
  }

  async function refreshListAndDetail(nextSelectedUserId = query.selectedUserId) {
    const next = { ...query, selectedUserId: nextSelectedUserId };
    writeQueryState(next);
    setQuery(next);
  }

  async function handleSave() {
    if (!selectedUser || !editForm) {
      return;
    }

    setIsSaving(true);
    setDetailError(null);
    setFormNotice(null);

    try {
      await apiFetch(`/platform/users/${selectedUser.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          role: editForm.role,
          status: editForm.status,
        }),
      });

      setFormNotice("User updates were saved on the mounted admin contract.");
      await refreshListAndDetail(selectedUser.id);
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : "Failed to update user.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleBanToggle() {
    if (!selectedUser) {
      return;
    }

    setIsBanning(true);
    setDetailError(null);
    setFormNotice(null);

    try {
      if (selectedUser.status === "disabled") {
        await apiFetch(`/platform/users/${selectedUser.id}`, {
          method: "PATCH",
          body: JSON.stringify({ status: "active" }),
        });
        setFormNotice("User access restored.");
      } else {
        await apiFetch(`/platform/users/${selectedUser.id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason: "disabled_from_admin_users" }),
        });
        setFormNotice("User access disabled.");
      }

      await refreshListAndDetail(selectedUser.id);
    } catch (error) {
      setDetailError(error instanceof Error ? error.message : "Failed to update user access.");
    } finally {
      setIsBanning(false);
    }
  }

  const disabledCount = useMemo(
    () => users.filter((user) => user.status === "disabled").length,
    [users],
  );

  if (listLoading) {
    return <UsersSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Users</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Mounted control-plane identity management for super admins. This surface edits canonical roles and ban state without exposing tokens, password hashes, or provider credentials.
        </p>
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          setQueryState({ search: searchDraft.trim(), offset: 0 });
        }}
        className="flex flex-col gap-3 lg:flex-row"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Search users by name or email"
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-10 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          />
        </div>
        <select
          value={query.role}
          onChange={(event) => setQueryState({ role: event.target.value, offset: 0 })}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <select
          value={query.status}
          onChange={(event) => setQueryState({ status: event.target.value, offset: 0 })}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-white hover:bg-[var(--color-surface)]"
        >
          Apply
        </button>
      </form>

      {listError && users.length === 0 ? (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-sm text-muted-foreground">
          {listError}
        </div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)]">
          {users.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={<UserRound className="h-8 w-8" />}
                title="No users match this view"
                description="Adjust the current filters or wait for new authenticated users to enter the control-plane workspace."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-[0.08em] text-muted-foreground">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">2FA</th>
                    <th className="px-4 py-3">Organizations</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Last active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const isSelected = user.id === query.selectedUserId;

                    return (
                      <tr
                        key={user.id}
                        onClick={() => setQueryState({ selectedUserId: user.id })}
                        className={`cursor-pointer border-b border-[var(--color-border)] transition-colors hover:bg-[var(--color-surface)]/70 ${isSelected ? "bg-[var(--color-surface)]/70" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{user.name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{titleizeRole(user.role)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {user.twoFactorEnabled ? "Enabled" : "Not enabled"}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {user.organizations.length > 0 ? user.organizations.join(", ") : "No org memberships"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusTone(user.status)}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{formatDateTime(user.lastActiveAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-[var(--color-border)] px-4 py-3 text-sm text-muted-foreground">
            <span>
              Showing {users.length} of {pagination.total} users, with {disabledCount} currently disabled
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQueryState({ offset: Math.max(query.offset - PAGE_SIZE, 0) })}
                disabled={query.offset === 0}
                className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setQueryState({ offset: query.offset + PAGE_SIZE })}
                disabled={!pagination.hasMore}
                className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </section>

        <aside className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div>
            <h2 className="text-lg font-semibold text-white">User detail</h2>
            <p className="mt-1 text-sm text-muted-foreground">{selectedUserLabel}</p>
          </div>

          {detailLoading ? (
            <div className="mt-4 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-28 w-full rounded-lg" />
            </div>
          ) : !query.selectedUserId ? (
            <div className="mt-6">
              <EmptyState
                icon={<ShieldCheck className="h-8 w-8" />}
                title="Select a user"
                description="Choose a user row to review identity details, role, and access state."
              />
            </div>
          ) : detailError ? (
            <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 text-sm text-muted-foreground">
              {detailError}
            </div>
          ) : selectedUser && editForm ? (
            <div className="mt-4 space-y-4">
              {formNotice ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
                  {formNotice}
                </div>
              ) : null}

              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p>Created {formatDateTime(selectedUser.createdAt)}.</p>
                    <p>Last active: {formatDateTime(selectedUser.lastActiveAt)}.</p>
                    <p>Updated: {formatDateTime(selectedUser.updatedAt)}.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">Role</label>
                <select
                  value={editForm.role}
                  onChange={(event) =>
                    setEditForm((current) => current ? { ...current, role: event.target.value as UserRole } : current)
                  }
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
                >
                  {ROLE_OPTIONS.filter((option) => option.value !== "all").map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-muted-foreground">Status</label>
                <select
                  value={editForm.status}
                  onChange={(event) =>
                    setEditForm((current) => current ? { ...current, status: event.target.value as UserStatus } : current)
                  }
                  className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white"
                >
                  <option value="active">Active</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>

              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Organizations</p>
                {selectedUser.organizations.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedUser.organizations.map((organization) => (
                      <span
                        key={organization}
                        className="rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs text-muted-foreground"
                      >
                        {organization}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">No organization memberships recorded.</p>
                )}
              </div>

              <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-4 text-sm text-muted-foreground">
                <div className="flex items-start gap-2">
                  <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="space-y-1">
                    <p>Two-factor auth: {selectedUser.twoFactorEnabled ? "Enabled" : "Not enabled"}.</p>
                    <p>Ban reason: {selectedUser.banReason ?? "Not currently banned"}.</p>
                    <p>Credentials and provider tokens stay hidden from this surface by design.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={isSaving || isBanning}
                  className="inline-flex items-center gap-2 rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save changes
                </button>
                <button
                  type="button"
                  onClick={() => void handleBanToggle()}
                  disabled={isSaving || isBanning}
                  className="inline-flex items-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm text-white hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isBanning ? <RefreshCw className="h-4 w-4 animate-spin" /> : null}
                  {selectedUser.status === "disabled" ? "Restore access" : "Disable access"}
                </button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
